import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { ErrorMessage, Field, FormikProvider, useFormik } from "formik";
import { Spin } from "antd";
import { Col, Form, Row, Modal, Button } from "react-bootstrap";
import {
  POST_GetDepartmentDropDownList,
  POST_GetNABLScopeDetailByID,
  POST_NABLScopeInsert,
  POST_NABLScopeUpdate,
  POST_NABLScopeUploadDocuments,
} from "../../../helpers/url_helper";
import CustomSelect from "../../../CustomControls/CustomSelect";
import { post, postFormData } from "../../../helpers/api_helper";
import CustomFlatpickr from "../../../CustomControls/CustomFlatpickr";
import CustomInput from "../../../CustomControls/CustomInput";
import CustomPhoneInput from "../../../CustomControls/CustomPhoneInput";
import {
  delete_RecordWithoutApi,
  errorMessage,
  successMessage,
  validateMobileNoField,
} from "../CommonFunctions";
import { SVGICON } from "../../../constant/theme";
import moment from "moment";

const AddNABLScope = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const { PrimaryIdParam } = useParams();

  const [country, setCountry] = useState("");
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);

  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));

  const validationSchema = Yup.object().shape({
    DepartmentIDEncrypted: Yup.string()
      .trim()
      .required("Department is required field"),
    AuditDate: Yup.string().trim().required("Audit Date is required field"),
    ValidFrom: Yup.string().trim().required("Valid From is required field"),
    ValidTo: Yup.string().trim().required("Valid To is required field"),
    NABLRepresentativeName: Yup.string()
      .trim()
      .required("NABL Representative is required field"),
    AuditorName: Yup.string().trim().required("Auditor Name is required field"),
  });

  const formik = useFormik({
    validationSchema: validationSchema,
    onSubmit: (values, formikHelpers) =>
      handleValidSubmit(values, formikHelpers),
    initialValues: {
      NABLScopeIDEncrypted: PrimaryIdParam ? window.atob(PrimaryIdParam) : "",
      DepartmentIDEncrypted: "",
      AuditDate: new Date(),
      ValidFrom: new Date(),
      ValidTo: new Date(),
      NABLRepresentativeName: "",
      AuditorName: "",
      CountryCode: "+91",
      AuditorMobileNo: "",
      AuditorMobileNoFormat: "",
      ListOfDocuments: [],
    },
    validateOnBlur: false,
  });

  const {
    values: { AuditDate, ValidFrom, ValidTo, CountryCode, ListOfDocuments },
  } = formik;

  useEffect(() => {
    if (!AuditDate) {
      formik.setFieldValue("AuditDate", "");
    }
    if (!ValidFrom) {
      formik.setFieldValue("ValidFrom", "");
    }
    if (!ValidTo) {
      formik.setFieldValue("ValidTo", "");
    }

    if (ValidFrom && ValidFrom < AuditDate) {
      formik.setFieldValue("ValidFrom", AuditDate);
    }
    if (ValidTo && ValidTo < ValidFrom) {
      formik.setFieldValue("ValidTo", ValidFrom);
    }
  }, [AuditDate, ValidFrom, ValidTo]);

  const handleValidSubmit = (values) => {
    if (values?.AuditorMobileNo) {
      if (
        !validateMobileNoField(
          values?.AuditorMobileNoFormat,
          CountryCode,
          values?.AuditorMobileNo
        )
      ) {
        formik.setFieldError(
          "AuditorMobileNo",
          "Please enter a valid mobile number"
        );
        formik.setFieldTouched("AuditorMobileNo", true);
        return false;
      }
    }

    setIsLoading(true);
    let payload = {
      ...values,
      AuditDate: values?.AuditDate
        ? moment(values?.AuditDate).format("YYYY-MM-DD").replace(/^\-/, "")
        : "",
      ValidFrom: values?.ValidFrom
        ? moment(values?.ValidFrom).format("YYYY-MM-DD").replace(/^\-/, "")
        : "",
      ValidTo: values?.ValidTo
        ? moment(values?.ValidTo).format("YYYY-MM-DD").replace(/^\-/, "")
        : "",
      CountryCode: "+" + values?.CountryCode.replace(/\+/g, ""),
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
      CreatedByEncrypted: AuthUser?.ReferenceIDEncrypt,
    };

    post(PrimaryIdParam ? POST_NABLScopeUpdate : POST_NABLScopeInsert, payload)
      .then((response) => {
        setIsLoading(true);
        if (response?.IsSuccess) {
          if (values?.ListOfDocuments?.length > 0) {
            let formData = new FormData();
            formData.append(
              "NABLScopeIDEncrypted",
              PrimaryIdParam
                ? values?.NABLScopeIDEncrypted
                : response?.EncryptID
            );
            formData.append("CompanyIDEncrypted", AuthUser?.CompanyIDEncrypt);
            Promise.all(
              values?.ListOfDocuments?.map((x, i) => {
                formData.append(
                  `ListOfDocuments[${i}].NABLScopeDocumentIDEncrypted`,
                  x.NABLScopeDocumentIDEncrypted
                );
                formData.append(
                  `ListOfDocuments[${i}].DocumentFile`,
                  x.DocumentFile
                );
                formData.append(
                  `ListOfDocuments[${i}].DocumentFilePath`,
                  x.DocumentFilePath
                );
                formData.append(
                  `ListOfDocuments[${i}].DocumentFileName`,
                  x.DocumentFileName
                );
                formData.append(`ListOfDocuments[${i}].IsDeleted`, x.IsDeleted);
              })
            );

            postFormData(POST_NABLScopeUploadDocuments, formData)
              .then((res) => {
                if (res?.IsSuccess) {
                  successMessage(response?.Message); //show success message from main api response
                  setTimeout(() => {
                    navigate(-1);
                  }, 1000);
                } else {
                  errorMessage(res?.Message);
                  setIsLoading(false);
                }
              })
              .catch((error) => {
                console.log(error);
                setIsLoading(false);
              });
          } else {
            successMessage(response?.Message);
            setTimeout(() => {
              navigate(-1);
            }, 1000);
          }
        } else {
          errorMessage(response?.Message);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const getDepartmentDDL = async () => {
    setIsLoading(true);
    await post(POST_GetDepartmentDropDownList, {
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
    })
      .then((response) => {
        if (response?.IsSuccess) {
          setDepartmentOptions(
            response?.List?.map((item) => ({
              label: item?.DepartmentName,
              value: item?.DepartmentIDEncrypted,
            }))
          );
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getDepartmentDDL();
    if (PrimaryIdParam) {
      getNABLScopeDetailByID(window.atob(PrimaryIdParam));
    }
  }, []);

  const uploadDocument = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      //console.log(file);
      if (file) {
        formik.setFieldValue("ListOfDocuments", [
          ...ListOfDocuments,
          {
            NABLScopeDocumentIDEncrypted: "",
            DocumentFile: file,
            viewFile: URL.createObjectURL(file),
            DocumentFileName: file?.name,
            DocumentFilePath: "",
            IsDeleted: false,
          },
        ]);
      }
    }
  };

  const deleteDocument = (i) => {
    if (ListOfDocuments[i]?.NABLScopeDocumentIDEncrypted) {
      formik.setFieldValue(`ListOfDocuments[${i}].IsDeleted`, true);
    } else {
      formik.setFieldValue(
        "ListOfDocuments",
        ListOfDocuments?.filter((x, ix) => ix != i)
      );
    }
  };

  const getNABLScopeDetailByID = async (nablScopeID) => {
    setIsLoading(true);
    await post(POST_GetNABLScopeDetailByID, {
      NABLScopeIDEncrypted: nablScopeID,
    })
      .then((response) => {
        if (response?.IsSuccess) {
          formik.setValues({
            ...response,
            AuditDate: response?.AuditDate ? new Date(response?.AuditDate) : "",
            ValidFrom: response?.ValidFrom ? new Date(response?.ValidFrom) : "",
            ValidTo: response?.ValidTo ? new Date(response?.ValidTo) : "",
            ListOfDocuments: response?.ListOfDocuments?.map((x) => {
              return {
                ...x,
                DocumentFile: "",
                viewFile: "",
                IsDeleted: false,
              };
            }),
          });
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  return (
    <div className="card m-1">
      <div className="card-body p-1">
        <Spin
          size="large"
          spinning={isLoading}
          tip={"Loading..."}
          style={{ position: "fixed" }}
        >
          <div className="tbl-caption p-2">
            <h4 className="heading mb-0">
              {PrimaryIdParam ? "Edit" : "Add"} NABL Scope
            </h4>
          </div>
          <hr className="mt-0 mb-1"></hr>
          <div className="p-2 pt-0">
            <FormikProvider value={formik}>
              <Form onSubmit={formik.handleSubmit}>
                <Row>
                  <Col lg={3}>
                    <Field
                      label="Department"
                      name="DepartmentIDEncrypted"
                      placeholder={"Select Department"}
                      component={CustomSelect}
                      options={departmentOptions}
                      required
                      autoFocus
                    />
                  </Col>
                  <Col lg={3}>
                    <Field
                      label="Audit Date"
                      name="AuditDate"
                      placeholder={"Select Audit Date"}
                      component={CustomFlatpickr}
                      required
                      options={{
                        altInput: true,
                        altFormat: "j-M-Y",
                        dateFormat: "d-m-Y",
                      }}
                    />
                  </Col>
                  <Col lg={3}>
                    <Field
                      label="Valid From"
                      name="ValidFrom"
                      placeholder={"Select Valid From"}
                      component={CustomFlatpickr}
                      required
                      options={{
                        altInput: true,
                        altFormat: "j-M-Y",
                        dateFormat: "d-m-Y",
                        minDate: AuditDate ? new Date(AuditDate) : "",
                      }}
                    />
                  </Col>
                  <Col lg={3}>
                    <Field
                      label="Valid To"
                      name="ValidTo"
                      placeholder={"Select Valid To"}
                      component={CustomFlatpickr}
                      required
                      options={{
                        altInput: true,
                        altFormat: "j-M-Y",
                        dateFormat: "d-m-Y",
                        minDate: ValidFrom ? new Date(ValidFrom) : "",
                      }}
                    />
                  </Col>
                  <Col lg={3}>
                    <Field
                      label="NABL Representative"
                      name="NABLRepresentativeName"
                      placeholder={"Lab Team Member Name"}
                      component={CustomInput}
                      maxlength={100}
                      required
                    />
                  </Col>
                  <Col lg={3}>
                    <Field
                      label="Auditor Name"
                      name="AuditorName"
                      placeholder={"Auditor Name"}
                      component={CustomInput}
                      maxlength={100}
                      required
                    />
                  </Col>
                  <Col lg={3}>
                    <Field
                      label="Auditor Mobile No."
                      name="AuditorMobileNo"
                      placeholder={"Enter Auditor Mobile No."}
                      country={country}
                      countryCode={CountryCode}
                      component={CustomPhoneInput}
                      onChange={(value, { countryCode, dialCode }) => {
                        formik.setFieldValue("CountryCode", dialCode);
                        formik.setFieldValue(
                          "AuditorMobileNo",
                          value.substring(dialCode.toString().length)
                        );
                      }}
                      onFocus={(value, { format }) => {
                        formik.setFieldValue("AuditorMobileNoFormat", format);
                      }}
                    />
                  </Col>
                  <Col lg={3}>
                    <label>Documents </label>
                    <div className="mb-2">
                      <div className="input-group">
                        <span className="form-control form-control-sm ps-2">
                          <Link
                            to="#"
                            className="text-primary"
                            onClick={() => {
                              setShowDocumentsModal(true);
                            }}
                          >
                            View Documents
                          </Link>
                        </span>
                        <div
                          className="input-group-text"
                          title="Choose File"
                          style={{
                            borderRadius: "0 0.375rem 0.375rem 0",
                          }}
                        >
                          <div className="open-file">
                            <input
                              // type="file"
                              id="DocumentUpload"
                              ////accept="image/png, image/gif, image/jpeg"
                              onClick={() => {
                                setShowDocumentsModal(true);
                              }}
                            />
                            <label htmlFor="DocumentUpload" name=""></label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
                <hr className="mt-1 mb-2"></hr>
                <button type="submit" className="btn btn-primary btn-sm me-2">
                  <i className="fa-solid fa-floppy-disk"></i> Submit
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={(e) => {
                    navigate(-1);
                  }}
                >
                  <i className="fa fa-cancel"></i> Cancel
                </button>
              </Form>
            </FormikProvider>
          </div>
        </Spin>

        <Modal
          className="fade"
          show={showDocumentsModal}
          onHide={setShowDocumentsModal}
          backdrop="static"
        >
          <Spin
            size="large"
            spinning={isLoading}
            tip={"Loading..."}
            style={{ position: "fixed" }}
          >
            <Modal.Header>
              <Modal.Title>Documents</Modal.Title>
              <Button
                type="button"
                variant=""
                className="btn-close"
                onClick={() => setShowDocumentsModal(false)}
              ></Button>
            </Modal.Header>
            <Modal.Body>
              <Row>
                <Col lg={12}>
                  <div className="mb-2">
                    <label></label>
                    <div className="avatar-file">
                      <input
                        type="file"
                        id="DocumentUpload2"
                        //accept="image/png, image/gif, image/jpeg"
                        onChange={uploadDocument}
                      />
                      <label htmlFor="DocumentUpload2" name=""></label>
                    </div>
                  </div>
                </Col>
                <Col lg={12}>
                  <div className="table-responsive active-projects style-1 shorting mb-2">
                    <div
                      id="Doc-tbl_wrapper"
                      className="dataTables_wrapper no-footer"
                    >
                      <table
                        id="Doc-tbl"
                        className="table dataTable no-footer mb-0"
                      >
                        <thead>
                          <tr>
                            <th width="4%"></th>
                            <th width="96%" style={{ textAlign: "left" }}>
                              File Name
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {ListOfDocuments?.length > 0 ? (
                            ListOfDocuments?.map((item, indx) => (
                              <tr
                                key={indx}
                                style={{
                                  display: item.IsDeleted == true ? "none" : "",
                                }}
                              >
                                <td>
                                  <Link
                                    to="#"
                                    // className="icon-box icon-box-md bg-primary-light me-1"
                                    className="icon-box icon-box-md me-1"
                                    title="View"
                                    onClick={() => {
                                      window.open(
                                        item.DocumentFilePath
                                          ? item.DocumentFilePath
                                          : item.viewFile
                                      );
                                    }}
                                  >
                                    {SVGICON.View}
                                  </Link>
                                  <Link
                                    to="#"
                                    className="icon-box icon-box-md me-1"
                                    title="Delete"
                                    onClick={() => {
                                      //deleteLetterRefDoc(indx);
                                      delete_RecordWithoutApi(
                                        deleteDocument,
                                        indx
                                      );
                                    }}
                                  >
                                    {SVGICON.DeleteDanger}
                                  </Link>
                                </td>
                                <td style={{ textAlign: "left" }}>
                                  {item.DocumentFileName}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={2} style={{ textAlign: "left" }}>
                                <div>{"No Record Found..."}</div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Col>
              </Row>
            </Modal.Body>
          </Spin>
        </Modal>
      </div>
    </div>
  );
};

export default AddNABLScope;
